import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../services/api'

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: true,
            isLoggingIn: false,
            error: null,

            // Login
            login: async (login, password) => {
                set({ isLoggingIn: true, error: null })
                try {
                    const response = await api.post('/auth/login', { login, password })
                    const { user, accessToken, refreshToken } = response.data.data

                    set({
                        user,
                        accessToken,
                        refreshToken,
                        isAuthenticated: true,
                        isLoggingIn: false,
                        error: null
                    })

                    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
                    return { success: true }
                } catch (error) {
                    const code = error.response?.data?.code || 'LOGIN_FAILED'
                    set({ isLoggingIn: false, error: code })
                    return { success: false, code }
                }
            },

            // Logout
            logout: async () => {
                try {
                    await api.post('/auth/logout')
                } catch (error) {
                    // Ignore logout errors
                }

                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    error: null
                })

                delete api.defaults.headers.common['Authorization']
            },

            // Check auth status
            checkAuth: async () => {
                const { accessToken } = get()

                if (!accessToken) {
                    set({ isLoading: false })
                    return
                }

                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

                try {
                    const response = await api.get('/auth/me')
                    set({
                        user: response.data.data.user,
                        isAuthenticated: true,
                        isLoading: false
                    })
                } catch (error) {
                    // Try to refresh token
                    const { refreshToken } = get()
                    if (refreshToken) {
                        try {
                            const refreshResponse = await api.post('/auth/refresh', { refreshToken })
                            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshResponse.data.data

                            api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`

                            const meResponse = await api.get('/auth/me')

                            set({
                                user: meResponse.data.data.user,
                                accessToken: newAccessToken,
                                refreshToken: newRefreshToken,
                                isAuthenticated: true,
                                isLoading: false
                            })
                            return
                        } catch (refreshError) {
                            // Refresh failed
                        }
                    }

                    // Clear auth state
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isLoading: false
                    })
                    delete api.defaults.headers.common['Authorization']
                }
            },

            // Update user
            updateUser: (userData) => {
                set({ user: { ...get().user, ...userData } })
            },

            // Check permission
            hasPermission: (permission) => {
                const { user } = get()
                if (!user?.permissions) return false
                if (user.permissions.includes('*')) return true
                return user.permissions.includes(permission)
            },

            // Check any permission
            hasAnyPermission: (permissions) => {
                const { user } = get()
                if (!user?.permissions) return false
                if (user.permissions.includes('*')) return true
                return permissions.some(p => user.permissions.includes(p))
            },

            // Is SuperAdmin
            isSuperAdmin: () => {
                const { user } = get()
                return user?.permissions?.includes('*') || false
            },

            // Clear error
            clearError: () => set({ error: null })
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                accessToken: state.accessToken,
                refreshToken: state.refreshToken
            })
        }
    )
)
